import { useCallback, useEffect, useRef, useState } from 'react'
import type ReCAPTCHA from 'react-google-recaptcha'
import { useCausesPortalAnalytics } from '../useCausesDashboardAnalytics/useCausesDashboardAnalytics'
import { CaptchaEventName } from '../useCausesDashboardAnalytics/causeDashboardAnalytics.types'

export type CaptchaFlow = 'sign_up' | 'add_organisation' | 'request_to_join' | 'applications'

export const CAPTCHA_SLOW_AFTER_MS = 10000

type Grecaptcha = { execute?: unknown; enterprise?: { execute?: unknown } }

// Checks for the function the widget calls rather than the global, which the loader defines before the
// rest of reCAPTCHA has arrived.
const isRecaptchaReady = () => {
  const { grecaptcha } = window as { grecaptcha?: Grecaptcha }

  return typeof grecaptcha?.execute === 'function' || typeof grecaptcha?.enterprise?.execute === 'function'
}

/*
  executeAsync never settles when reCAPTCHA can't load. The slow state lets callers explain the wait
  without abandoning a challenge the user may still be solving, so it stays off once reCAPTCHA has loaded.
*/
export function useCaptcha(flow: CaptchaFlow, eventContext: Record<string, unknown> = {}) {
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const slowTimerRef = useRef<number>()
  const eventContextRef = useRef(eventContext)
  eventContextRef.current = eventContext
  const { track } = useCausesPortalAnalytics()
  const [isVerifying, setIsVerifying] = useState(false)
  const [isSlow, setIsSlow] = useState(false)

  useEffect(() => () => window.clearTimeout(slowTimerRef.current), [])

  const verify = useCallback(
    async (recaptcha: ReCAPTCHA) => {
      const startedAt = Date.now()
      const eventProperties = () => ({
        ...eventContextRef.current,
        flow,
        recaptchaReady: isRecaptchaReady(),
        durationMs: Date.now() - startedAt
      })

      setIsVerifying(true)
      setIsSlow(false)
      track(CaptchaEventName.VERIFICATION_STARTED, eventProperties())
      // A second call before the first settles would otherwise orphan the first slow timer.
      window.clearTimeout(slowTimerRef.current)
      slowTimerRef.current = window.setTimeout(() => {
        const properties = eventProperties()
        setIsSlow(!properties.recaptchaReady)
        track(CaptchaEventName.VERIFICATION_SLOW, properties)
      }, CAPTCHA_SLOW_AFTER_MS)

      try {
        const token = await recaptcha.executeAsync()
        track(CaptchaEventName.VERIFICATION_SUCCEEDED, { ...eventProperties(), hasToken: !!token })

        return token
      } catch (error) {
        track(CaptchaEventName.VERIFICATION_FAILED, {
          ...eventProperties(),
          reason: error instanceof Error ? error.message : String(error)
        })
        throw error
      } finally {
        window.clearTimeout(slowTimerRef.current)
        setIsVerifying(false)
        setIsSlow(false)
        // Invisible reCAPTCHA keeps its solved token until reset, so a retry would replay a used one.
        recaptcha.reset()
      }
    },
    [flow, track]
  )

  const executeCaptcha = useCallback(() => {
    const recaptcha = recaptchaRef.current

    if (!recaptcha) {
      track(CaptchaEventName.VERIFICATION_SKIPPED, { ...eventContextRef.current, flow, reason: 'no_widget' })

      return Promise.resolve(undefined)
    }

    return verify(recaptcha)
  }, [flow, track, verify])

  return { recaptchaRef, executeCaptcha, isVerifying, isSlow }
}
